import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, OverlayView } from '@react-google-maps/api';
import {
  FaTruck, FaGasPump, FaClock, FaBolt,
  FaMapMarkerAlt, FaCheckCircle, FaExclamationTriangle,
  FaTimesCircle, FaClipboardCheck
} from 'react-icons/fa';
import { useApiCall } from '../hooks/useApiCall';
import '../styles/LiveFueling.css';

const CENTER = { lat: 5.811487489669329, lng: -55.1971283564614 };

const MAP_OPTIONS = {
  mapTypeId: 'satellite', tilt: 0,
  zoomControl: true, mapTypeControl: false,
  streetViewControl: false, rotateControl: false,
  fullscreenControl: true, scaleControl: true,
};

function TankerMarker({ tanker, active }) {
  const pct   = tanker.level_pct ?? 0;
  const fuel  = tanker.fuelType  || '';
  const name  = tanker.name      || tanker.qr_token || '?';
  const fuelColor = pct < 20 ? '#ef4444' : pct < 40 ? '#f59e0b' : '#22c55e';
  const accent    = active ? '#ef4444' : '#f5af18';
  const bg        = active ? '#1a0a0a' : '#161106';

  return (
    <div style={{ position: 'relative', transform: 'translate(-50%, -100%)', userSelect: 'none' }}>
      {active && (
        <span style={{
          position: 'absolute', top: 0, left: '50%', transform: 'translate(-50%, -50%)',
          width: 52, height: 52, borderRadius: '50%',
          background: 'rgba(239,68,68,0.25)',
          animation: 'lf-pulse 1.6s ease-out infinite',
          pointerEvents: 'none',
        }} />
      )}
      <div style={{
        background: bg,
        border: `2px solid ${accent}`,
        borderRadius: 10,
        padding: '6px 10px',
        minWidth: 130,
        boxShadow: `0 4px 18px rgba(0,0,0,0.55), 0 0 0 1px ${accent}33`,
        backdropFilter: 'blur(4px)',
      }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
          <span style={{
            background: accent, borderRadius: 6, width: 22, height: 22,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <FaTruck style={{ color: '#fff', fontSize: 11 }} />
          </span>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', letterSpacing: 0.3 }}>
            {name}
          </span>
          {active && (
            <span style={{
              marginLeft: 'auto', background: '#ef444422', border: '1px solid #ef4444',
              borderRadius: 4, padding: '1px 5px', fontSize: 9, color: '#ef4444', fontWeight: 700,
              letterSpacing: 0.5, whiteSpace: 'nowrap',
            }}>LIVE</span>
          )}
        </div>

        {/* Fuel level bar */}
        <div style={{ marginBottom: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
            <span style={{ color: '#8896a5', fontSize: 9 }}>Fuel level</span>
            <span style={{ color: fuelColor, fontSize: 10, fontWeight: 700 }}>{pct}%</span>
          </div>
          <div style={{ height: 4, background: '#ffffff18', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: pct + '%', background: fuelColor, borderRadius: 3, transition: 'width 0.4s' }} />
          </div>
        </div>

        {/* Fuel type */}
        {fuel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <FaGasPump style={{ color: accent, fontSize: 9 }} />
            <span style={{ color: '#aab4be', fontSize: 10 }}>{fuel}</span>
          </div>
        )}
      </div>

      {/* Callout tail */}
      <div style={{
        width: 0, height: 0, margin: '0 auto',
        borderLeft: '7px solid transparent',
        borderRight: '7px solid transparent',
        borderTop: `8px solid ${accent}`,
      }} />
    </div>
  );
}

function PulsingDot({ color = '#35c068' }) {
  return <span className="pulsing-dot" style={{ background: color }} />;
}

function MiniTankBar({ pct }) {
  const color = pct < 20 ? '#ef4444' : pct < 40 ? '#f59e0b' : '#22c55e';
  return (
    <div className="lf-mini-tank">
      <div className="lf-mini-track">
        <div className="lf-mini-fill" style={{ height: pct + '%', background: color }} />
      </div>
      <span className="lf-mini-pct" style={{ color }}>{pct}%</span>
    </div>
  );
}

function ApronMap({ activeTankers, idleTankers }) {
  const mapRef = useRef(null);
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '' });

  const fitMarkers = useCallback((map) => {
    const all = [...activeTankers, ...idleTankers].filter(t => t.lat && t.lng);
    if (!map) return;
    if (all.length === 0) {
      map.setCenter(CENTER);
      map.setZoom(18);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    all.forEach(t => bounds.extend({ lat: t.lat, lng: t.lng }));
    map.fitBounds(bounds);
    // Don't zoom in too far when there's only one marker
    if (all.length === 1) map.setZoom(Math.min(map.getZoom(), 18));
  }, [activeTankers, idleTankers]);

  const handleLoad = useCallback((map) => {
    mapRef.current = map;
    fitMarkers(map);
  }, [fitMarkers]);

  // Re-fit whenever tanker data changes
  useEffect(() => {
    fitMarkers(mapRef.current);
  }, [fitMarkers]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && mapRef.current) {
        window.google?.maps?.event?.trigger(mapRef.current, 'resize');
        fitMarkers(mapRef.current);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [fitMarkers]);

  if (!isLoaded) return <div className="lf-map-placeholder">Loading map…</div>;

  return (
    <div className="lf-map-wrapper">
      <GoogleMap mapContainerClassName="lf-map-container" defaultCenter={CENTER} defaultZoom={18} options={MAP_OPTIONS} onLoad={handleLoad}>
        {activeTankers.map(t => (
          <OverlayView
            key={`a-${t.id}`}
            position={(t.lat && t.lng) ? { lat: t.lat, lng: t.lng } : CENTER}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <TankerMarker tanker={t} active={true} />
          </OverlayView>
        ))}
        {idleTankers.map(t => (
          <OverlayView
            key={`i-${t.id}`}
            position={(t.lat && t.lng) ? { lat: t.lat, lng: t.lng } : CENTER}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <TankerMarker tanker={t} active={false} />
          </OverlayView>
        ))}
      </GoogleMap>
      <div className="lf-map-legend">
        <div className="lf-legend-item"><span className="lf-legend-dot red" />Active</div>
        <div className="lf-legend-item"><span className="lf-legend-dot yellow" />Idle</div>
      </div>
    </div>
  );
}

export default function LiveFueling() {
  const apiCall = useApiCall();
  const [activeTankers, setActiveTankers] = useState([]);
  const [idleTankers, setIdleTankers]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [lastUpdated, setLastUpdated]     = useState(null);

  const load = useCallback(() => {
    apiCall('admin/live-fueling', 'POST').then(res => {
      if (res?.success) {
        setActiveTankers(res.data.active_tankers || []);
        setIdleTankers(res.data.idle_tankers || []);
        setLastUpdated(new Date());
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);

  const all = [...activeTankers, ...idleTankers];

  return (
    <div className="page-container">
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1>Live Fueling</h1>
          <p className="page-subtitle">Real-time view of active fueling sessions · auto-refresh every 3 s</p>
        </div>
        <div className="lf-header-status">
          <PulsingDot />
          <span>{activeTankers.length} active · {idleTankers.length} idle</span>
        </div>
      </div>

      {/* ── Stats row ── */}
      <div className="lf-stats-row">
        <div className="lf-stat-card">
          <div className="lf-stat-icon" style={{ background: 'rgba(219,49,49,0.12)', color: '#db3131' }}><FaBolt /></div>
          <div>
            <div className="lf-stat-value">{activeTankers.length}</div>
            <div className="lf-stat-label">Active tankers</div>
          </div>
        </div>
        <div className="lf-stat-card">
          <div className="lf-stat-icon" style={{ background: 'rgba(245,175,24,0.12)', color: '#f5af18' }}><FaTruck /></div>
          <div>
            <div className="lf-stat-value">{idleTankers.length}</div>
            <div className="lf-stat-label">Idle tankers</div>
          </div>
        </div>
        <div className="lf-stat-card">
          <div className="lf-stat-icon" style={{ background: 'rgba(53,192,104,0.12)', color: '#35c068' }}><FaGasPump /></div>
          <div>
            <div className="lf-stat-value">{all.length}</div>
            <div className="lf-stat-label">Total tankers</div>
          </div>
        </div>
        <div className="lf-stat-card">
          <div className="lf-stat-icon" style={{ background: 'rgba(77,168,255,0.12)', color: '#4da8ff' }}><FaClock /></div>
          <div>
            <div className="lf-stat-value" style={{ fontSize: 13 }}>
              {lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}
            </div>
            <div className="lf-stat-label">Last updated</div>
          </div>
        </div>
      </div>

      {/* ── Left map / Right panels ── */}
      <div className="lf-layout">

        {/* Left: sticky map */}
        <div className="lf-col-left">
          <ApronMap activeTankers={activeTankers} idleTankers={idleTankers} />
        </div>

        {/* Right: Active + Idle stacked */}
        <div className="lf-col-right">

        {/* Active Tankers */}
        <div className="lf-col">
          <div className="lf-section-heading">
            <FaBolt style={{ color: '#db3131' }} />
            <span>Active Tankers</span>
            {activeTankers.length === 0 && <span className="lf-muted-note">No tankers in use right now</span>}
          </div>

          {loading ? (
            <div className="lf-card"><div className="lf-empty">Loading…</div></div>
          ) : activeTankers.length === 0 ? (
            <div className="lf-card lf-card-empty">
              <FaCheckCircle style={{ fontSize: 28, color: '#35c068', marginBottom: 8 }} />
              <div style={{ fontWeight: 600 }}>No active fueling sessions</div>
              <div className="lf-muted-note" style={{ marginTop: 4 }}>Tankers will appear here when in use</div>
            </div>
          ) : (
            <div className="lf-active-list">
              {activeTankers.map(t => {
                const clr = t.level_pct < 20 ? '#ef4444' : t.level_pct < 40 ? '#f59e0b' : '#22c55e';
                return (
                  <div key={t.id} className="lf-active-card">
                    <div className="lf-active-card-accent" />
                    <div className="lf-active-card-body">
                      <div className="lf-active-top">
                        <div className="lf-active-name">
                          <FaTruck style={{ color: '#db3131' }} /> {t.name}
                        </div>
                        <div className="lf-live-badge"><PulsingDot /> LIVE</div>
                      </div>
                      <div className="lf-active-badges">
                        <span className="fuel-type-badge"><FaGasPump style={{ fontSize: 9 }} /> {t.fuelType}</span>
                        {t.location && (
                          <span className="lf-loc-badge"><FaMapMarkerAlt style={{ fontSize: 9 }} /> {t.location}</span>
                        )}
                      </div>
                      <div className="lf-active-level-row">
                        <MiniTankBar pct={t.level_pct} />
                        <div className="lf-active-level-text">
                          <span style={{ color: clr, fontWeight: 700, fontSize: 22 }}>{t.level_pct}%</span>
                          <span className="lf-small-muted">{t.amount} / {t.amount_cap} {t.amount_type}</span>
                          {t.level_pct < 20 && <span className="lf-low-warning"><FaExclamationTriangle style={{ fontSize: 10 }} /> Low fuel</span>}
                          {t.bowser_check === 'fail' && <span className="lf-bowser-warning lf-bowser-fail"><FaTimesCircle style={{ fontSize: 10 }} /> Bowser check failed</span>}
                          {t.bowser_check === 'not_checked' && <span className="lf-bowser-warning lf-bowser-none"><FaClipboardCheck style={{ fontSize: 10 }} /> No bowser check today</span>}
                        </div>
                      </div>
                      <div className="lf-flow-bar"><div className="lf-flow-fill" /></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Idle Tankers */}
        <div className="lf-col" style={{ marginTop: 4 }}>
          <div className="lf-section-heading">
            <FaTruck style={{ color: '#f5af18' }} />
            <span>Idle Tankers</span>
            {idleTankers.length === 0 && <span className="lf-muted-note">All tankers are active</span>}
          </div>

          <div className="lf-card">
            {loading ? (
              <div className="lf-empty">Loading…</div>
            ) : idleTankers.length === 0 ? (
              <div className="lf-empty">All tankers are currently active</div>
            ) : idleTankers.map(t => {
              const clr = t.level_pct < 20 ? '#ef4444' : t.level_pct < 40 ? '#f59e0b' : '#22c55e';
              return (
                <div key={t.id} className="lf-tanker-row">
                  <MiniTankBar pct={t.level_pct} />
                  <div className="lf-tanker-row-info">
                    <div className="lf-tanker-row-name">
                      <FaTruck style={{ color: '#f5af18', fontSize: 11 }} /> {t.name}
                    </div>
                    <div className="lf-tanker-row-sub">
                      <span className="fuel-type-badge" style={{ fontSize: 10 }}><FaGasPump style={{ fontSize: 9 }} /> {t.fuelType}</span>
                      {t.location && <span style={{ color: '#8896a5' }}><FaMapMarkerAlt style={{ fontSize: 9 }} /> {t.location}</span>}
                    </div>
                  </div>
                  <div className="lf-tanker-row-right">
                    <span style={{ color: clr, fontWeight: 700, fontSize: 15 }}>{t.level_pct}%</span>
                    <span className="lf-small-muted">{t.amount}/{t.amount_cap} {t.amount_type}</span>
                    {t.bowser_check === 'fail' && <span className="lf-bowser-warning lf-bowser-fail"><FaTimesCircle style={{ fontSize: 9 }} /> Bowser fail</span>}
                    {t.bowser_check === 'not_checked' && <span className="lf-bowser-warning lf-bowser-none"><FaClipboardCheck style={{ fontSize: 9 }} /> No check</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        </div>{/* lf-col-right */}
      </div>
    </div>
  );
}

